import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { ViewColumn as ViewColumnIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import type { OnChangeFn, VisibilityState } from '@tanstack/react-table';
import { SEARCE_COLORS } from '../../theme';

export type ColumnVisibilityMenuItem = {
  id: string;
  label: string;
  /** When false, column is always shown and omitted from the menu. Default true. */
  hideable?: boolean;
};

export type ColumnVisibilityMenuProps = {
  items: ColumnVisibilityMenuItem[];
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  /** Anchor trigger label. */
  triggerLabel?: string;
};

function formatColumnIdAsLabel(id: string): string {
  return id
    .replace(/-/g, ' ')
    .split('_')
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/** TanStack `VisibilityState` + menu with select all / clear all. */
export function ColumnVisibilityMenu({
  items,
  columnVisibility,
  onColumnVisibilityChange,
  triggerLabel = 'Toggle Columns',
}: ColumnVisibilityMenuProps): React.ReactElement {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const hideableItems = useMemo(() => items.filter((i) => i.hideable !== false), [items]);

  const visibleHideableCount = useMemo(
    () => hideableItems.filter((i) => columnVisibility[i.id] !== false).length,
    [hideableItems, columnVisibility]
  );

  const allHideableVisible = visibleHideableCount === hideableItems.length;

  const handleSelectAll = useCallback(() => {
    onColumnVisibilityChange({});
  }, [onColumnVisibilityChange]);

  /** Leave only the first hideable column visible (cannot hide every column). */
  const handleClearAll = useCallback(() => {
    if (hideableItems.length === 0) return;
    onColumnVisibilityChange(() => {
      const next: VisibilityState = {};
      hideableItems.forEach((item, index) => {
        next[item.id] = index === 0;
      });
      return next;
    });
  }, [hideableItems, onColumnVisibilityChange]);

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'nowrap',
        verticalAlign: 'middle',
      }}
    >
      <Button
        size="small"
        variant="text"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        aria-controls={anchorEl ? 'column-visibility-menu' : undefined}
        startIcon={<ViewColumnIcon sx={{ fontSize: 18 }} />}
        sx={{
          color: SEARCE_COLORS.primary,
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '0.8125rem',
          py: 0.25,
          px: 0.75,
          flexShrink: 0,
          '&:hover': { backgroundColor: alpha(SEARCE_COLORS.primary, 0.08) },
        }}
      >
        {triggerLabel}
      </Button>
      <Menu
        id="column-visibility-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { minWidth: 260, maxHeight: 400 },
          },
        }}
      >
        <Box sx={{ px: 1.25, pt: 1, pb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>
            Columns
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            Choose which columns appear in the table.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, px: 1.25, pb: 0.75, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="text"
            onClick={handleSelectAll}
            disabled={hideableItems.length === 0 || allHideableVisible}
            sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, px: 0.75 }}
          >
            Select all
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={handleClearAll}
            disabled={hideableItems.length <= 1 || visibleHideableCount <= 1}
            sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, px: 0.75 }}
          >
            Clear all
          </Button>
        </Box>
        <Divider sx={{ my: 0.25 }} />
        <MenuItem disabled sx={{ opacity: 1, py: 0.5, minHeight: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Visible columns
          </Typography>
        </MenuItem>
        {hideableItems.map((item) => {
          const checked = columnVisibility[item.id] !== false;
          const disableUncheck = checked && visibleHideableCount <= 1;
          const label = item.label || formatColumnIdAsLabel(item.id);
          return (
            <MenuItem
              key={item.id}
              dense
              disableRipple
              onClick={(e) => e.stopPropagation()}
              sx={{ py: 0.25, px: 1 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={checked}
                    disabled={disableUncheck}
                    onChange={(_, next) => {
                      onColumnVisibilityChange((prev) => ({
                        ...prev,
                        [item.id]: next,
                      }));
                    }}
                  />
                }
                label={label}
                sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' } }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}

export { formatColumnIdAsLabel };
